<template>
  <article id="geometric-correction">
    <h1>几何校正</h1>

    <section id="geo-correction-intro">
      <h2>一、简介</h2>
      <p>
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;几何校正旨在消除遥感影像中的空间几何畸变，使其与真实地理位置一一对应，生成具有地理参考信息的地表反射率产品（GeoTIFF）。本模块支持使用影像的 RPC模型、轨道参数、姿态信息 等方式进行正射校正或系统校正，并自动重采样至指定投影和分辨率。
      </p>
      <p>
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;本软件集成高精度几何校正引擎，能够根据不同传感器模型自适应选择校正方法，广泛适用于国产多颗卫星的高分辨率影像处理。
      </p>
    </section>

    <section id="geo-correction-interface">
      <h2>二、功能界面</h2>
      <div class="image-section">
        <div class="image-container">
          <img src="@/assets/documentation/jihe.png" alt="几何校正界面" class="software-image" />
          <p class="image-caption">图6：几何校正界面 - 消除影像几何畸变，生成具有地理参考信息的产品</p>
        </div>
      </div>
      <p>界面布局包括：</p>
      <ul>
        <li>输入影像路径选择</li>
        <li>投影坐标系与输出分辨率设置</li>
        <li>DEM路径选择（可选）</li>
        <li>RPC模型/轨道信息显示</li>
        <li>输出路径设置</li>
        <li>执行校正按钮 + 进度提示栏</li>
      </ul>
      <p class="note">🗺️ 几何校正是遥感影像处理的最后一步，通过RPC模型或轨道参数消除影像的几何畸变，生成具有精确地理参考信息的GeoTIFF产品。</p>
    </section>

    <section id="geo-correction-params">
      <h2>三、用户参数</h2>
      <table>
        <thead>
          <tr>
            <th>参数名称</th>
            <th>功能说明</th>
            <th>获取方式</th>
            <th>示例值</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>输入影像路径</td>
            <td>已完成大气校正的地表反射率影像</td>
            <td>用户选择</td>
            <td>D:\data\GF1_L2_ref.tif</td>
          </tr>
          <tr>
            <td>RPC系数文件</td>
            <td>卫星影像的几何模型文件</td>
            <td>自动提取或手动加载</td>
            <td>GF1_L2_ref.RPB</td>
          </tr>
          <tr>
            <td>DEM文件路径</td>
            <td>地形数据（提高正射精度）</td>
            <td>可选（推荐使用）</td>
            <td>D:\data\SRTM_DEM.tif</td>
          </tr>
          <tr>
            <td>输出投影类型</td>
            <td>输出坐标系统（如WGS84/UTM等）</td>
            <td>默认读取输入投影</td>
            <td>EPSG:4326 或 EPSG:32649</td>
          </tr>
          <tr>
            <td>输出分辨率</td>
            <td>校正后影像的空间分辨率（单位：米）</td>
            <td>手动设置</td>
            <td>10</td>
          </tr>
          <tr>
            <td>重采样方式</td>
            <td>像素插值算法</td>
            <td>默认设置为双线性</td>
            <td>Nearest / Bilinear / Cubic</td>
          </tr>
          <tr>
            <td>输出路径</td>
            <td>输出几何校正后的影像保存目录</td>
            <td>用户设置</td>
            <td>D:\data\GF1\output_geo\</td>
          </tr>
        </tbody>
      </table>
      <p class="note">✅ 如果影像已内嵌 RPC 模型信息，则系统自动识别；否则需手动加载。</p>
    </section>

    <section id="geo-correction-steps">
      <h2>四、操作步骤</h2>
      <ol>
        <li>
          <strong>加载输入影像</strong>
          <ul>
            <li>选择经过大气校正处理后的 .tif 影像；</li>
            <li>系统自动识别 RPC 模型或提示导入外部 RPB 文件。</li>
          </ul>
        </li>
        <li>
          <strong>加载 DEM 数据（可选）</strong>
          <ul>
            <li>支持用户指定 SRTM、ASTER 或高分 DEM；</li>
            <li>有助于提升山区或倾斜角度较大地区的校正精度。</li>
          </ul>
        </li>
        <li>
          <strong>设置输出参数</strong>
          <ul>
            <li>设置输出分辨率与目标投影系统（默认自动匹配）；</li>
            <li>可选择重采样算法。</li>
          </ul>
        </li>
        <li>
          <strong>执行几何校正</strong>
          <ul>
            <li>系统调用高精度校正模块，支持多线程处理；</li>
            <li>自动记录姿态、位置、RPC参数与误差残差信息。</li>
          </ul>
        </li>
        <li>
          <strong>保存与输出</strong>
          <ul>
            <li>输出具备地理参考信息的 GeoTIFF 影像；</li>
            <li>自动生成处理日志和影像报告。</li>
          </ul>
        </li>
      </ol>
    </section>

    <section id="geo-correction-results">
      <h2>五、用户结果</h2>
      <table>
        <thead>
          <tr>
            <th>输出文件名</th>
            <th>类型</th>
            <th>内容说明</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>GF1_WFV_20230510_L3_geo.tif</td>
            <td>GeoTIFF影像</td>
            <td>已完成几何校正的地表反射率产品</td>
          </tr>
          <tr>
            <td>GF1_WFV_20230510_L3_geo.log</td>
            <td>日志文件</td>
            <td>包含投影参数、误差评估、处理流程等信息</td>
          </tr>
          <tr>
            <td>meta_GF1_geo.json</td>
            <td>元数据文件</td>
            <td>存储输入参数、重采样方式、输出投影等信息</td>
          </tr>
          <tr>
            <td>GF1_WFV_20230510_L3_geo.kml</td>
            <td>可选输出</td>
            <td>用于 Google Earth 可视化（如勾选该选项）</td>
          </tr>
        </tbody>
      </table>
      <p class="note">📌 最终影像可直接用于 GIS 系统叠加、制图、分类分析或高阶反演建模。</p>
      <p class="note">✅ 支持 ENVI、ArcGIS、QGIS、MapGIS 等主流遥感平台的加载和分析。</p>
    </section>
  </article>
</template>

<script>
export default {
  name: 'GeometricCorrection',
  props: {
    targetSectionId: {
      type: String,
      default: null,
    },
  },
  watch: {
    targetSectionId(newId) {
      if (newId) {
        this.$emit('scroll-to-section', newId);
      }
    },
  },
  mounted() {
    if (this.targetSectionId) {
      this.$emit('scroll-to-section', this.targetSectionId);
    }
  },
};
</script>

<style scoped>
/* 图片展示样式 */
.image-section {
  margin: 30px 0;
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.image-container {
  text-align: center;
  background: rgba(var(--card-bg), 0.8);
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(var(--primary), 0.1);
  border: 1px solid rgba(var(--primary), 0.1);
}

.software-image {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.software-image:hover {
  transform: scale(1.02);
  box-shadow: 0 6px 25px rgba(0, 0, 0, 0.3);
}

.image-caption {
  margin-top: 15px;
  font-size: 14px;
  color: var(--text-secondary);
  font-style: italic;
  line-height: 1.5;
}

/* 添加计数器重置 */
ol {
  counter-reset: list-item;
}

/* 重用 Document.vue 中的表格和列表样式 */
.main-content table {
  width: 100%;
  border-collapse: collapse;
  margin: 25px 0;
  font-size: 15px;
  text-align: left;
  box-shadow: 0 0 15px rgba(var(--primary), 0.1);
  border-radius: 8px;
  overflow: hidden;
}

.main-content th,
.main-content td {
  padding: 12px 15px;
  border: 1px solid rgba(var(--primary), 0.2);
  color: var(--text-primary);
}

.main-content thead tr {
  background-color: rgba(var(--primary), 0.2);
  color: var(--primary);
  text-shadow: 0 0 5px rgba(var(--primary), 0.5);
  font-weight: bold;
}

.main-content tbody tr {
  background-color: rgba(var(--card-bg), 0.6);
}

.main-content tbody tr:nth-of-type(even) {
  background-color: rgba(var(--card-bg), 0.8);
}

.main-content tbody tr:hover {
  background-color: rgba(var(--primary), 0.1);
  color: var(--primary);
  text-shadow: 0 0 3px rgba(var(--primary), 0.3);
}

/* List Styles (Ordered and Unordered) */
.main-content ol,
.main-content ul {
  list-style: none;
  padding-left: 20px;
  margin: 20px 0;
}

.main-content ol li,
.main-content ul li {
  margin-bottom: 10px;
  color: var(--text-primary);
  line-height: 1.6;
  position: relative;
}

/* 新增：将 counter-increment 添加到 ol li */
ol li {
  counter-increment: list-item;
}

ol li strong,
ul li strong {
  color: var(--primary);
  text-shadow: 0 0 5px rgba(var(--primary), 0.4);
}

ol li::before {
  content: counter(list-item) ". ";
  color: var(--secondary);
  font-weight: bold;
  position: absolute;
  left: -25px;
  top: 0;
  width: 20px;
  text-align: right;
  text-shadow: 0 0 5px rgba(var(--secondary), 0.5);
}

.main-content ul li::before {
  content: "\2705";
  color: var(--secondary);
  font-size: 1.1em;
  position: absolute;
  left: -50px;
  top: 0;
  text-shadow: 0 0 5px rgba(var(--secondary), 0.5);
}

.main-content ul li p {
  margin-left: 0;
  margin-bottom: 5px;
}

.main-content p.note {
  background-color: rgba(var(--secondary), 0.1);
  border-left: 5px solid var(--secondary);
  padding: 10px 15px;
  margin-top: 20px;
  border-radius: 4px;
  color: var(--text-primary);
  font-size: 14px;
}
</style> 